import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { testimonials } from "@/data/content";

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const go = (d) => {
    setDir(d);
    setIndex((i) => (i + d + testimonials.length) % testimonials.length);
  };

  const t = testimonials[index];
  const initials = t.name.split(" ").map((w) => w[0]).slice(0, 2).join("");

  return (
    <section id="depoimentos" data-testid="testimonials-section" className="relative overflow-hidden bg-white py-20 sm:py-28 lg:py-32">
      <div className="absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-brand-orange/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="06"
          kicker="Depoimentos"
          title="Quem vive a comunidade, reconhece"
          description="Palavras de quem convive com o trabalho da Rosangela há anos — do campo à cidade."
        />

        <div className="relative">
          <Quote className="absolute -top-6 left-0 h-16 w-16 text-brand-light/30" aria-hidden="true" />
          <div className="relative min-h-[16rem] sm:min-h-[13rem]">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.blockquote
                key={index}
                custom={dir}
                initial={{ opacity: 0, x: dir * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -60 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid="testimonial-card"
                className="rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-10 shadow-sm"
              >
                <p className="text-lg sm:text-xl leading-relaxed text-slate-700">“{t.text}”</p>
                <footer className="mt-6 flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-royal to-brand-dark font-display text-sm font-extrabold text-white">
                    {initials}
                  </span>
                  <span>
                    <span className="block font-display text-base font-bold text-slate-900">{t.name}</span>
                    <span className="block text-sm text-slate-500">{t.role}</span>
                  </span>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="flex gap-2" role="tablist" aria-label="Selecionar depoimento">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`testimonial-dot-${i}`}
                  aria-label={`Depoimento ${i + 1}`}
                  onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
                  className={`h-2.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-brand-orange" : "w-2.5 bg-slate-300 hover:bg-brand-light"}`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="testimonial-prev"
                aria-label="Depoimento anterior"
                onClick={() => go(-1)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition-all hover:border-brand-royal hover:bg-brand-royal hover:text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                data-testid="testimonial-next"
                aria-label="Próximo depoimento"
                onClick={() => go(1)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition-all hover:border-brand-royal hover:bg-brand-royal hover:text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
